import { Project } from '../types';

type ProjectContentOverride = Pick<Project, 'description' | 'content' | 'websiteUrl'>;

type OverrideMatcher = {
  ids?: string[];
  titles?: string[];
};

const sevenoirOverride: ProjectContentOverride = {
  description:
    'Sevenoir is building infrastructure for the identity, recovery, and handoff of physical belongings across institutions, creating one trusted record from intake to verified return. Starting with university campuses as the first commercial beachhead.',
  content: `Sevenoir has evolved through several iterations, but the underlying thesis has remained consistent: physical belongings lose continuity when they move between people, systems, and institutions.

The project began with persistent identity for physical assets, later explored aviation baggage custody and evidence, and has now evolved into institutional recovery infrastructure.

The current product is built around one trusted record that follows an item from intake through each handoff, verification step, and eventual release or return. Rather than treating recovery as a series of disconnected actions, Sevenoir creates a structured layer for identity, responsibility, and continuity around physical belongings.

University campuses are the first commercial beachhead because they provide a clear environment to validate the workflow, measure operational value, and refine the system. They are not the boundary of the company.

The long-term ambition is broader: to build infrastructure for how institutions identify, transfer, verify, recover, and return physical belongings across complex physical environments.`,
  websiteUrl: 'https://sevenoir.com'
};

const overrideEntries: Array<{
  matcher: OverrideMatcher;
  override: ProjectContentOverride;
}> = [
  {
    matcher: {
      ids: ['sevenoir'],
      titles: ['sevenoir']
    },
    override: sevenoirOverride
  }
];

const normalize = (value?: string | null) =>
  String(value || '').trim().toLowerCase();

export const getProjectContentOverride = (
  projectId?: string | null,
  projectTitle?: string | null
): ProjectContentOverride | undefined => {
  const normalizedId = normalize(projectId);
  const normalizedTitle = normalize(projectTitle);

  return overrideEntries.find(({ matcher }) => {
    const idMatch = matcher.ids?.some(
      id => normalize(id) === normalizedId
    );

    const titleMatch = matcher.titles?.some(
      title => normalize(title) === normalizedTitle
    );

    return Boolean(idMatch || titleMatch);
  })?.override;
};
